/**
 * 路由表。
 *
 * 关键决定：**`/chat` 与 `/chat/:id` 是同一条路由**（可选参数），而不是两条。
 * 原因：会话创建后要把 URL 从 `/chat` 换成 `/chat/<id>`，而此时正在流式接收回答。
 * 两条路由会让 vue-router 卸载并重挂 `ChatScreen`——组件一换，
 * `useChatController` 里的 AbortController、消息列表、节流缓冲全部消失，
 * 用户的第一个问题会在读答案读到一半时凭空消失。
 *
 * 标题：Next 那边是 `metadata` 导出（静态），这里用手写的 `TITLE_MAP` + afterEach。
 * 不用 `route.meta.title` 是因为 `/:id` 这类动态段的标题要在进入后才确定，
 * 集中在一处映射比散落在各条路由上更容易看出全貌。
 *
 * 后台（`/admin/**`）用 `meta.roles` 声明"谁能进"，判定集中在唯一的 `beforeEach` 里
 * （判定本体在 `./guard`，这里只负责接线）：门禁写在路由定义旁边，加一条后台路由的人
 * 一眼就能看出漏没漏 `roles`；而"未登录 → 登录页并带回跳地址 / 角色不符 → 403"
 * 这套流程只有一份实现。组件一律懒加载，所以判定不通过时后台那几份 chunk 根本不会被请求。
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import ChatView from '../views/ChatView.vue';
import CheckoutView from '../views/CheckoutView.vue';
import ConversationsView from '../views/ConversationsView.vue';
import HomeView from '../views/HomeView.vue';
import LoginView from '../views/LoginView.vue';
import NotFoundView from '../views/NotFoundView.vue';
import ProfileView from '../views/ProfileView.vue';
import QuotaView from '../views/QuotaView.vue';
import { getAuthState, whenAuthReady } from '../lib/auth-store';
import { ADMIN_ROLES, ROOT_ROLES, createAdminGuard } from './guard';

const APP_NAME = '智家教育';
const DEFAULT_TITLE = `${APP_NAME} · 文化服务平台`;

/** 路由名 → 页面标题（模板 `%s · 智家教育`）。 */
const TITLE_MAP: Readonly<Record<string, string>> = {
  home: DEFAULT_TITLE,
  chat: 'AI 咨询',
  checkout: '购买套餐',
  conversations: '我的会话',
  login: '登录 / 注册',
  profile: '我的画像',
  quota: '额度与消耗',
  notFound: '页面不存在',
  adminOverview: '后台概览',
  adminUsers: '用户管理',
  adminUserDetail: '用户详情',
  adminConversations: '会话管理',
  adminUsage: '用量明细',
  adminChannels: '渠道管理',
  adminModelRoutes: '模型优先级',
  adminEmailChannels: '邮箱渠道',
  adminAccounts: '管理员账户',
  adminForbidden: '无访问权限',
};

/**
 * 后台路由。
 *
 * `component` 一律是动态 `import()`：普通用户（以及绝大多数访客）永远不会下载后台的
 * 任何一行代码。这也让"守卫比组件先跑"成为事实——判定不通过时那几份 chunk 根本不会发出请求。
 *
 * **`meta.roles` 不写在父路由上**：父级 meta 会合并进每个子路由，若父级写 `['admin','root']`，
 * 那么 `/admin/403` 自己也会要求管理员角色——普通用户被送来 403、403 又把他送回来，
 * 直接构成死循环（vue-router 会终止导航并报错）。所以角色声明只落在需要门禁的子路由上，
 * 403 页面本身永远无门禁。
 */
const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    component: () => import('../views/admin/AdminLayoutView.vue'),
    children: [
      // 路径写空串而不是让父路由自己 redirect：这样 `/admin` 与 `/admin/overview`
      // 共用同一个 AdminLayoutView 实例，父布局不会被卸载重挂。
      { path: '', redirect: { name: 'adminOverview' } },
      { path: 'overview', name: 'adminOverview', component: () => import('../views/admin/AdminOverviewView.vue'), meta: { roles: ADMIN_ROLES } },
      { path: 'users', name: 'adminUsers', component: () => import('../views/admin/AdminUsersView.vue'), meta: { roles: ADMIN_ROLES } },
      { path: 'users/:id', name: 'adminUserDetail', component: () => import('../views/admin/AdminUserDetailView.vue'), meta: { roles: ADMIN_ROLES } },
      { path: 'conversations', name: 'adminConversations', component: () => import('../views/admin/AdminConversationsView.vue'), meta: { roles: ADMIN_ROLES } },
      // 额度与渠道是"发钱"的口子，管理员只看得到概览 / 用户 / 会话。
      { path: 'usage', name: 'adminUsage', component: () => import('../views/admin/AdminUsageView.vue'), meta: { roles: ROOT_ROLES } },
      { path: 'channels', name: 'adminChannels', component: () => import('../views/admin/AdminChannelsView.vue'), meta: { roles: ROOT_ROLES } },
      // 模型优先级承接了原渠道上的三个模型字段：它决定"全站默认走哪个模型、不可用时降级到谁"，
      // 与渠道页同级而不是并进去——渠道页只管连接参数，这里只管模型与顺序。
      { path: 'model-routes', name: 'adminModelRoutes', component: () => import('../views/admin/AdminModelRoutesView.vue'), meta: { roles: ROOT_ROLES } },
      // 邮箱渠道与上游渠道同级：两者都是"改错了会让全站某条链路不可用"的配置，
      // 但它是 ROOT 专属（发信密钥同样是外泄即事故），所以与渠道并列而不是并进渠道页。
      { path: 'email-channels', name: 'adminEmailChannels', component: () => import('../views/admin/AdminEmailChannelsView.vue'), meta: { roles: ROOT_ROLES } },
      { path: 'accounts', name: 'adminAccounts', component: () => import('../views/admin/AdminAccountsView.vue'), meta: { roles: ROOT_ROLES } },
      // 无门禁：被上面几条拒绝的人正需要落到这里。（名字与标题都在表里，刷新后仍是这个 URL。）
      { path: '403', name: 'adminForbidden', component: () => import('../views/admin/AdminForbiddenView.vue') },
    ],
  },
];


export const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: HomeView },
  // 可选参数：一个组件实例贯穿"新会话 → 已有会话"的整个过程。
  { path: '/chat/:id?', name: 'chat', component: ChatView },
  { path: '/checkout', name: 'checkout', component: CheckoutView },
  { path: '/conversations', name: 'conversations', component: ConversationsView },
  { path: '/login', name: 'login', component: LoginView },
  { path: '/profile', name: 'profile', component: ProfileView },
  { path: '/quota', name: 'quota', component: QuotaView },
  ...adminRoutes,
  { path: '/:pathMatch(.*)*', name: 'notFound', component: NotFoundView },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // 单页应用里保留滚动位置在切换路由时几乎总是错的：
    // 用户点进一个新页面，期待的是从顶部开始读。
    return { top: 0 };
  },
});

/**
 * 门禁接线。
 *
 * `whenAuthReady` 而不是 `getAuthState().hydrated`：冷启动深链时 `hydrated` 还是 false
 * 而 `main.ts` 的恢复流程正在飞，读标志会立刻按空状态判"未登录"，把已登录的 ROOT
 * 闪去登录页；等这个单飞 Promise 才有同步点。判定规则本身见 `./guard`。
 */
router.beforeEach(
  createAdminGuard({
    getAuthState,
    whenAuthReady,
  }),
);

router.afterEach((to) => {
  const name = typeof to.name === 'string' ? to.name : 'notFound';
  const title = TITLE_MAP[name] ?? DEFAULT_TITLE;
  document.title = title === DEFAULT_TITLE ? title : `${title} · ${APP_NAME}`;
});
